import React, { type ReactNode } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, RotateCcwIcon } from 'lucide-react';
import { cn } from '../../src/lib/utils';
import { useFitScale } from '../../hooks/useFitScale';
import { useSlideIndex } from './SlideIndexContext';
export const TOTAL_SLIDES = 22;

/** Fixed design canvas. Every slide is authored at this size and scaled to fit. */
const STAGE_W = 1920;
const STAGE_H = 1080;
type SlideProps = {
  children: ReactNode;
  /** Fallback 1-based slide number; the deck registry supplies the real one. */
  index?: number;
  /** Discreet source / methodology note for any external statistics. */
  source?: string;
  /** `deep` is the near-black cover / closing treatment. */
  variant?: 'default' | 'deep';
  className?: string;
};

/**
 * Full-bleed 16:9 institutional slide shell. Renders a fixed 1920×1080 stage
 * (a size container, so slides use `cqw`/`cqh` units) and scales it uniformly
 * to the viewport — identical layout on phone, tablet and desktop.
 */
export function Slide({
  children,
  index,
  source,
  variant = 'default',
  className
}: SlideProps) {
  const {
    ref,
    scale,
    portrait
  } = useFitScale(STAGE_W, STAGE_H);
  const position = useSlideIndex();
  const pageNumber = position?.index ?? index ?? 1;
  const pageTotal = position?.total ?? TOTAL_SLIDES;
  const background = variant === 'deep' ? '#04060C' : '#070B14';
  return <section ref={ref} className="relative h-full w-full overflow-hidden" style={{
    background,
    fontFamily: 'var(--font-sans)'
  }} data-id="element-17" data-mp="2190yv3duv3d8">
      <div className="absolute left-1/2 top-1/2 flex flex-col overflow-hidden text-[#EEF2F9] [container-type:size]" style={{
      width: STAGE_W,
      height: STAGE_H,
      background,
      transform: `translate(-50%, -50%) scale(${scale})`,
      transformOrigin: 'center center'
    }} data-id="element-18" data-mp="1x90kfiqras66">
        {/* Fine institutional grid — meaningful structure, not decoration. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.55]" style={{
        backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.035) 1px, transparent 1px)',
        backgroundSize: '6.25% 11.11%'
      }} data-id="element-19" data-mp="1dhdr23sacj0t" />

        <div className={cn('relative flex min-h-0 w-full flex-1 flex-col px-[5.5%] pb-[3cqh] pt-[6cqh]', className)} data-id="element-20" data-mp="3s9supe6y1ea4">
          {children}
        </div>

        <div className="relative mx-[5.5%] mb-[3.2cqh] flex items-center justify-between border-t border-white/10 pt-[1.6cqh]" data-id="element-21" data-mp="2zo13gvux8tfq">
          <span className="text-[clamp(7px,0.72cqw,15px)] uppercase tracking-[0.28em] text-white/35" data-id="element-22" data-mp="38md3tpllb5ld">
            StartupFuel · DiligenceGPT™
          </span>
          {source ? <span className="mx-[2cqw] flex-1 truncate text-center text-[clamp(6.5px,0.66cqw,14px)] text-white/25" data-id="element-23" data-mp="jx51qa5lewmo">
              {source}
            </span> : <span className="flex-1" data-id="element-24" data-mp="28dn372179w1o" />}
          <span className="text-[clamp(7px,0.72cqw,15px)] tracking-[0.28em] text-white/35" data-id="element-25" data-mp="24cbc0s2oh2rd">
            {String(pageNumber).padStart(2, '0')} / {pageTotal}
          </span>
        </div>
      </div>

      <TouchNav isFirst={pageNumber <= 1} isLast={pageNumber >= pageTotal} data-id="element-26" data-mp="1unjn6sswyyko" />

      {portrait && <div className="pointer-events-none absolute inset-x-0 bottom-[6%] flex items-center justify-center gap-2 px-6 text-center text-xs tracking-wide text-white/45" data-id="element-27" data-mp="1pogixakrcwdr">
          <RotateCcwIcon className="h-4 w-4 shrink-0" aria-hidden data-id="element-28" data-mp="1kr6tvw1a6mqg" />
          <span data-id="element-29" data-mp="1gnsdh49fk4ch">Rotate your phone for full-screen slides</span>
        </div>}
    </section>;
}

/** Asks the deck harness to move one slide, using its own navigation message. */
function requestSlide(direction: 'prev' | 'next') {
  window.postMessage({
    type: 'MP_NAVIGATE_SLIDE',
    direction
  }, '*');
}
const navButton = 'pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-[#070B14]/80 text-white/80 backdrop-blur-sm transition active:scale-95 active:bg-white/10 disabled:opacity-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B8CFF]';

/**
 * Previous / next buttons for touch devices (no keyboard). Hidden on
 * mouse-driven screens so desktop presenting stays chrome-free.
 */
function TouchNav({
  isFirst,
  isLast
}: {
  isFirst: boolean;
  isLast: boolean;
}) {
  return <nav aria-label="Slide navigation" className="pointer-events-none absolute inset-x-0 bottom-0 z-20 hidden items-end justify-between p-3 [@media(pointer:coarse)]:flex" style={{
    paddingBottom: 'max(12px, env(safe-area-inset-bottom))'
  }} data-id="element-30" data-mp="1or8dlah6opfv">
      <button type="button" aria-label="Previous slide" disabled={isFirst} onClick={() => requestSlide('prev')} className={navButton} data-id="element-31" data-mp="1ezm4bjoep0fr">
        <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-32" data-mp="160h1w29q4myb" />
      </button>
      <button type="button" aria-label="Next slide" disabled={isLast} onClick={() => requestSlide('next')} className={navButton} data-id="element-33" data-mp="mg9wkiwr4ao5">
        <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-34" data-mp="wdxqx7qg3g5s" />
      </button>
    </nav>;
}